import { map, shareReplay } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Organization } from '../../models/entities/organization';
import { OrganizationBroadcastService, OrganizationEventType } from './organization-broadcast.service';

export interface OrganizationV2DTO
{
    id: number;
    name: string;
    trialExpires: number | null;
    hasPaymentMethod: boolean;
    isResponsibleForPlatformBilling?: boolean;
    profile: {
        timezone: string;
        absorbsCCFees: boolean;
        absorbsACHFees: boolean;
    };
    preferences: {
        useCashAccounting: boolean;
    };
}

@Injectable({
    providedIn: 'root',
})
export class OrganizationV2Service
{
    private orgMap = new Map<number, Observable<OrganizationV2DTO>>();

    constructor(
            private _http: ApplicationHttpClient,
            private _broadcast: OrganizationBroadcastService,
    ) {
        this.initializeBroadcastListener();
    }

    /**
     * @param {number} organizationId
     * @returns {Observable<Organization>}
     */
    public getOrganizationById(organizationId: number): Observable<OrganizationV2DTO> {
        if (this.orgMap.has(organizationId)) {
            return this.orgMap.get(organizationId)!;
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.OrganizationsV2Module.Organization(organizationId));
        const obs = this._http.get(url)
                .pipe(
                        map((response: Object) => {
                            return response as OrganizationV2DTO;
                        }),
                        shareReplay({bufferSize: 1, refCount: false}),
                );
        this.orgMap.set(organizationId, obs);
        return obs;
    }

    /**
     * Clear cached organization data
     * Useful when organization is modified and needs to be reloaded
     */
    public clearOrganization(organizationId: number): void {
        this.orgMap.delete(organizationId);
    }

    /**
     * Clear all cached organizations
     */
    public clearAll(): void {
        this.orgMap.clear();
    }

    /**
     * Initialize broadcast channel listener for organization updates
     */
    private initializeBroadcastListener(): void {
        this._broadcast.onEventType$(OrganizationEventType.ORGANIZATION_UPDATED).subscribe(event => {
            this.clearOrganization(event.organizationId);
        });
    }

}
